import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { integrationsApiService } from '../../api/integrations-api';
import { RootState } from '@/shared/store/store';
import { selectIntegrationById } from '../selectors';
import {
  fetchIntegrations,
  fetchIntegrationsSuccess,
  fetchIntegrationSuccess,
  createIntegrationSuccess,
  updateIntegrationSuccess,
  deleteIntegrationSuccess,
  toggleIntegrationStatusSuccess,
  testConnectionSuccess,
  fetchStatsSuccess,
  integrationsFailure,
} from '../integrations-slice';

const selectIntegrationsQuery = (state: RootState) => ({
  pagination: state.integrations.pagination,
  filters: state.integrations.filters,
});

function* refetchIntegrationsWithCurrentQuery(): Generator<any, void, any> {
  const { pagination, filters } = (yield select(
    selectIntegrationsQuery,
  )) as ReturnType<typeof selectIntegrationsQuery>;

  const query = {
    page: pagination?.page || 1,
    limit: pagination?.limit || 10,
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.type ? { type: filters.type } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.isActive !== undefined ? { isActive: filters.isActive } : {}),
  };

  yield put(fetchIntegrations(query));
}

// Saga para obtener integraciones
function* fetchIntegrationsSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/fetchIntegrationsStart' });
    const response: any = yield call(
      integrationsApiService.getIntegrations,
      action.payload
    );
    yield put(fetchIntegrationsSuccess(response));
  } catch (error: any) {
    yield put(
      integrationsFailure(error.message || 'Error al obtener las integraciones')
    );
  }
}

// Saga para obtener una integración específica
function* fetchIntegrationSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/fetchIntegrationStart' });
    const { id } = action.payload;
    const response: any = yield call(integrationsApiService.getIntegration, id);
    yield put(fetchIntegrationSuccess(response));
  } catch (error: any) {
    yield put(
      integrationsFailure(error.message || 'Error al obtener la integración')
    );
  }
}

// Saga para crear integración
function* createIntegrationSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/createIntegrationStart' });
    const { data } = action.payload;
    const response: any = yield call(
      integrationsApiService.createIntegration,
      data
    );
    yield put(createIntegrationSuccess(response));
    yield* refetchIntegrationsWithCurrentQuery();
  } catch (error: any) {
    yield put(
      integrationsFailure(error.message || 'Error al crear la integración')
    );
  }
}

// Saga para actualizar integración
function* updateIntegrationSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/updateIntegrationStart' });
    const { id, data } = action.payload;
    const response: any = yield call(
      integrationsApiService.updateIntegration,
      id,
      data
    );
    yield put(updateIntegrationSuccess(response));
  } catch (error: any) {
    yield put(
      integrationsFailure(error.message || 'Error al actualizar la integración')
    );
  }
}

// Saga para eliminar integración
function* deleteIntegrationSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/deleteIntegrationStart' });
    const { id } = action.payload;
    yield call(integrationsApiService.deleteIntegration, id);
    yield put(deleteIntegrationSuccess(id));
  } catch (error: any) {
    yield put(
      integrationsFailure(error.message || 'Error al eliminar la integración')
    );
  }
}

// Saga para cambiar estado de integración
function* toggleIntegrationStatusSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/toggleIntegrationStatusStart' });
    const { id } = action.payload;

    const currentIntegration = (yield select((state: RootState) =>
      selectIntegrationById(id)(state)
    )) as ReturnType<ReturnType<typeof selectIntegrationById>>;

    const response: any = yield call(
      integrationsApiService.toggleIntegrationStatus,
      id
    );

    const updatedIntegration =
      response && response.id
        ? { ...currentIntegration, ...response }
        : currentIntegration
        ? {
            ...currentIntegration,
            status:
              currentIntegration.status === 'active' ? 'inactive' : 'active',
            isActive: currentIntegration.status !== 'active',
          }
        : response;

    yield put(toggleIntegrationStatusSuccess(updatedIntegration));
  } catch (error: any) {
    yield put(
      integrationsFailure(
        error.message || 'Error al cambiar el estado de la integración'
      )
    );
  }
}

// Saga para probar conexión
function* testConnectionSaga(
  action: PayloadAction<any>
): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/testConnectionStart' });
    const { id } = action.payload;
    const response: any = yield call(
      integrationsApiService.testConnection,
      id
    );
    // Después de probar, obtener la integración actualizada
    const integration: any = yield call(
      integrationsApiService.getIntegration,
      id
    );
    yield put(testConnectionSuccess(integration));
  } catch (error: any) {
    yield put(
      integrationsFailure(
        error.message || 'Error al probar la conexión de la integración'
      )
    );
  }
}

// Saga para obtener estadísticas
function* fetchStatsSaga(): Generator<any, void, any> {
  try {
    yield put({ type: 'integrations/fetchStatsStart' });
    const response: any = yield call(integrationsApiService.getStats);
    yield put(fetchStatsSuccess(response));
  } catch (error: any) {
    yield put(
      integrationsFailure(
        error.message || 'Error al obtener las estadísticas de integraciones'
      )
    );
  }
}

// Root saga para integrations
export function* integrationsSaga() {
  yield takeLatest('integrations/fetchIntegrations', fetchIntegrationsSaga);
  yield takeLatest('integrations/fetchIntegration', fetchIntegrationSaga);
  yield takeLatest('integrations/createIntegration', createIntegrationSaga);
  yield takeLatest('integrations/updateIntegration', updateIntegrationSaga);
  yield takeLatest('integrations/deleteIntegration', deleteIntegrationSaga);
  yield takeLatest(
    'integrations/toggleIntegrationStatus',
    toggleIntegrationStatusSaga
  );
  yield takeLatest('integrations/testConnection', testConnectionSaga);
  yield takeLatest('integrations/fetchStats', fetchStatsSaga);
}

